import type { LandingComponents } from "@/puck/blocks/blockTypes";

export const sectionConfigs: Pick<
  LandingComponents,
  "Logos" | "Workflows" | "Outcomes" | "BuyerQuote"
> = {
  Logos: {
    label: "Logo cloud",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "textarea", contentEditable: true },
      logos: {
        type: "array",
        min: 1,
        max: 12,
        getItemSummary: (item) => item.name,
        defaultItemProps: { name: "Partner" },
        arrayFields: {
          name: { type: "text", contentEditable: true },
        },
      },
    },
    defaultProps: {
      eyebrow: "Trusted workflows",
      title: "Built for teams that need speed, trust, and control",
      logos: [
        { name: "Voice" },
        { name: "SMS" },
        { name: "Email" },
        { name: "CRM" },
      ],
    },
    render: ({ eyebrow, title, logos }) => (
      <section className="w-full bg-white px-4 py-16">
        <div className="mx-auto w-full max-w-6xl text-center">
          {eyebrow ? (
            <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
              {eyebrow}
            </p>
          ) : null}
          <h2 className="mx-auto mt-4 max-w-3xl font-monument text-3xl font-bold leading-tight text-black md:text-5xl">
            {title}
          </h2>
          <div className="mt-10 grid grid-cols-2 gap-3 md:grid-cols-4">
            {logos.map((logo, index) => (
              <div
                key={`logo-${index}`}
                className="rounded-2xl border border-black/10 bg-marketing-surface px-4 py-6 font-monument text-sm text-black/60"
              >
                {logo.name}
              </div>
            ))}
          </div>
        </div>
      </section>
    ),
  },
  Workflows: {
    label: "Workflow cards",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "textarea", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      cards: {
        type: "array",
        min: 1,
        max: 6,
        getItemSummary: (item) => item.title,
        defaultItemProps: {
          badge: "01",
          title: "Workflow title",
          description: "Describe what this workflow helps the team do.",
        },
        arrayFields: {
          badge: { type: "text" },
          title: { type: "text", contentEditable: true },
          description: { type: "textarea", contentEditable: true },
        },
      },
    },
    defaultProps: {
      eyebrow: "Workflows",
      title: "Turn proven playbooks into AI-powered motions",
      subtitle: "Each card can describe a product workflow, industry use case, or campaign step.",
      cards: [
        {
          badge: "01",
          title: "Qualify",
          description: "Capture intent and ask the right questions before routing.",
        },
        {
          badge: "02",
          title: "Engage",
          description: "Follow up across channels with consistent context.",
        },
        {
          badge: "03",
          title: "Convert",
          description: "Book a meeting, collect a payment, or hand off to your team.",
        },
      ],
    },
    render: ({ eyebrow, title, subtitle, cards }) => (
      <section id="workflows" className="w-full bg-marketing-surface px-4 py-20 md:py-28">
        <div className="mx-auto w-full max-w-6xl">
          <div className="max-w-3xl">
            {eyebrow ? (
              <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-gail-primary">
                {eyebrow}
              </p>
            ) : null}
            <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight text-black md:text-6xl">
              {title}
            </h2>
            {subtitle ? (
              <p className="mt-5 font-monument text-base leading-relaxed text-black/55 md:text-lg">
                {subtitle}
              </p>
            ) : null}
          </div>
          <div className="mt-12 grid gap-4 md:grid-cols-3">
            {cards.map((card) => (
              <article
                key={`${card.badge}-${card.title}`}
                className="rounded-3xl border border-black/10 bg-white p-7 shadow-sm"
              >
                <p className="font-mono text-xs font-bold text-gail-primary">
                  {card.badge}
                </p>
                <h3 className="mt-8 font-monument text-2xl font-bold text-black">
                  {card.title}
                </h3>
                <p className="mt-4 font-monument text-sm leading-relaxed text-black/55">
                  {card.description}
                </p>
              </article>
            ))}
          </div>
        </div>
      </section>
    ),
  },
  Outcomes: {
    label: "Outcome metrics",
    fields: {
      eyebrow: { type: "text" },
      title: { type: "textarea", contentEditable: true },
      subtitle: { type: "textarea", contentEditable: true },
      metrics: {
        type: "array",
        min: 1,
        max: 4,
        getItemSummary: (item) => `${item.value} ${item.label}`,
        defaultItemProps: { value: "2x", label: "faster launch cycles" },
        arrayFields: {
          value: { type: "text", contentEditable: true },
          label: { type: "text", contentEditable: true },
        },
      },
    },
    defaultProps: {
      eyebrow: "Outcomes",
      title: "Make the business case obvious",
      subtitle: "Use outcomes, proof points, and operational metrics to anchor the page.",
      metrics: [
        { value: "24/7", label: "coverage" },
        { value: "6", label: "languages" },
        { value: "1", label: "publish flow" },
      ],
    },
    render: ({ eyebrow, title, subtitle, metrics }) => (
      <section className="w-full bg-gail-primary px-4 py-20 text-white md:py-28">
        <div className="mx-auto w-full max-w-6xl">
          <div className="grid gap-10 md:grid-cols-[1.1fr_0.9fr] md:items-end">
            <div>
              {eyebrow ? (
                <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-white/90">
                  {eyebrow}
                </p>
              ) : null}
              <h2 className="mt-5 font-monument text-4xl font-bold leading-[1.08] tracking-tight md:text-6xl">
                {title}
              </h2>
              {subtitle ? (
                <p className="mt-5 max-w-2xl font-monument text-base leading-relaxed text-white/90 md:text-lg">
                  {subtitle}
                </p>
              ) : null}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {metrics.map((metric) => (
                <div
                  key={`${metric.value}-${metric.label}`}
                  className="rounded-2xl bg-white/10 p-5"
                >
                  <p className="font-monument text-3xl font-bold">{metric.value}</p>
                  <p className="mt-2 font-monument text-sm text-white">
                    {metric.label}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    ),
  },
  BuyerQuote: {
    label: "Buyer quote",
    fields: {
      quote: { type: "textarea", contentEditable: true },
      name: { type: "text", contentEditable: true },
      title: { type: "text", contentEditable: true },
    },
    defaultProps: {
      quote:
        "Gail helped us turn a complex customer motion into a simple, measurable AI workflow.",
      name: "Marketing leader",
      title: "Reference customer",
    },
    render: ({ quote, name, title }) => (
      <section className="w-full bg-white px-4 py-20">
        <figure className="mx-auto max-w-4xl rounded-4xl border border-black/10 bg-marketing-voice-card p-8 md:p-12">
          <blockquote className="font-monument text-2xl font-bold leading-tight text-black md:text-4xl">
            &quot;{quote}&quot;
          </blockquote>
          <figcaption className="mt-8 font-monument text-sm text-gail-gray">
            <span className="font-bold text-black">{name}</span>
            {title ? `, ${title}` : ""}
          </figcaption>
        </figure>
      </section>
    ),
  },
};
